import { Link } from '@tanstack/react-router'

import { ACTIVITY_WINDOW_DAYS } from '@brickup/shared/constants'
import type {
  CustomerActivity,
  CustomerActivityKind,
} from '@brickup/shared/dto'
import { STAFF_DASHBOARD } from '@brickup/shared/locales/de/staff-dashboard'
import { TERMS } from '@brickup/shared/locales/de/terms'
import { matchLink, projectLink } from './dashboard-links'
import { Badge, INLINE_LINK_CLASS, type Tone } from '@/components/base'
import { EmptyState } from '@/components/EmptyState'
import { ChangeEntryRow } from '@/components/Internal/ChangeEntryRow'

const COPY = STAFF_DASHBOARD.projects

const KIND_TONE = {
  CONFIGURATOR: 'info',
  DOCUMENT_UPLOAD: 'info',
  DOCUMENT_VIEW: 'default',
  MATCH_LIKE: 'success',
  MATCH_DISLIKE: 'warning',
} as const satisfies Record<CustomerActivityKind, Tone>

/** What a customer did, one row each, with the way to where it happened. */
export function CustomerActivityFeed({
  activity,
}: {
  activity: CustomerActivity[]
}) {
  if (activity.length === 0) {
    return (
      <EmptyState
        layout="inline"
        title={COPY.noActivity(ACTIVITY_WINDOW_DAYS)}
      />
    )
  }

  return (
    <div className="flex flex-col">
      {activity.map((entry, index) => (
        <ChangeEntryRow
          key={`${entry.kind}-${entry.at}-${index}`}
          at={entry.at}
          actorName={entry.customerName ?? TERMS.customer}
          actorEmail={null}
          aside={
            <Badge
              size="sm"
              variant={KIND_TONE[entry.kind]}
              label={COPY.activityKinds[entry.kind]}
            />
          }
        >
          <ActivityBody entry={entry} />
        </ChangeEntryRow>
      ))}
    </div>
  )
}

function ActivityBody({ entry }: { entry: CustomerActivity }) {
  switch (entry.kind) {
    case 'CONFIGURATOR':
    case 'DOCUMENT_UPLOAD':
    case 'DOCUMENT_VIEW':
      return (
        <>
          <span className="text-[13px] text-foreground">
            {entry.kind === 'CONFIGURATOR'
              ? COPY.changedAnswers(entry.changeCount)
              : entry.documentTitle}
          </span>
          <Link
            {...projectLink(entry.project.id)}
            className={INLINE_LINK_CLASS}
          >
            {entry.project.name}
          </Link>
        </>
      )
    case 'MATCH_LIKE':
    case 'MATCH_DISLIKE':
      return (
        <>
          <Link
            {...matchLink(entry.propertyId, entry.mandateId)}
            className={INLINE_LINK_CLASS}
          >
            {COPY.matchedProperty(entry.propertyLabel)}
          </Link>
          {entry.feedback ? (
            <span className="text-[13px] italic text-foreground-2">
              {COPY.feedbackQuote(entry.feedback)}
            </span>
          ) : null}
        </>
      )
  }
}
